import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  useGetPurchaseOrderByIdQuery,
  useSendPurchaseOrderEmailMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

interface POEmailModalProps {
  isOpen: boolean;
  toggle: () => void;
  poId: number;
  poNumber: string;
}

const POEmailModal: React.FC<POEmailModalProps> = ({
  isOpen,
  toggle,
  poId,
  poNumber,
}) => {
  const { data: poData, isLoading: loadingPO } = useGetPurchaseOrderByIdQuery(
    poNumber,
    { skip: !isOpen || !poNumber }
  );
  const [sendPurchaseOrderEmail, { isLoading: isSending }] =
    useSendPurchaseOrderEmailMutation();

  const [toEmails, setToEmails] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachments, setAttachments] = useState<File[]>([]);

  const getSupplierEmailQty = (item: any) => {
    const ordered = parseFloat(String(item?.ordered_quantity ?? ""));
    if (!Number.isNaN(ordered) && ordered > 0) return ordered;
    return parseFloat(String(item?.quantity ?? 0)) || 0;
  };

  useEffect(() => {
    if (poData) {
      const isCancelled =
        poData.po_status === "cancelled" || !!poData.cancelled_at;
      setToEmails(poData.supplier_email || "");
      if (isCancelled) {
        setSubject(
          `Purchase Order Cancelled - ${poData.inventory_purchase_order_number}`
        );
        setBody(
          `<p>Please note that Purchase Order ${poData.inventory_purchase_order_number} has been cancelled.</p>` +
            (poData.cancellation_reason
              ? `<p>Reason: ${poData.cancellation_reason}</p>`
              : "") +
            "<p>The cancelled Purchase Order is attached for your reference.</p>"
        );
      } else {
        setSubject(
          `Purchase Order - ${poData.inventory_purchase_order_number}`
        );
        setBody(
          "<p>Please find attached the Purchase Order for your reference.</p><p>Thank you for your business!</p>"
        );
      }
    }
  }, [poData]);

  const generatePODetailsAndTableHTML = () => {
    if (!poData?.items?.length) return "";

    const items = poData.items as any[];
    const tableRows = items
      .map(
        (item: any, idx: number) => {
          const cost = item.effective_total_item_cost ?? item.total_item_cost;
          return `
        <tr>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${idx + 1}</td>
          <td style="padding: 10px; border: 1px solid #dee2e6; background-color: #ffffff;">${item.product_name || "N/A"}</td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${getSupplierEmailQty(item).toFixed(2)}</td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${item.measurement_name || "-"}</td>
          <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6; background-color: #ffffff;">${cost != null ? parseFloat(String(cost)).toFixed(2) : "-"}</td>
        </tr>`;
        }
      )
      .join("");

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0d6efd; padding-bottom: 15px;">
          <h2 style="color: #0d6efd; margin: 0; font-size: 24px;">PURCHASE ORDER DETAILS</h2>
        </div>
        <div style="margin-bottom: 20px;">
          <p style="margin: 5px 0;"><strong>PO Number:</strong> ${poData.inventory_purchase_order_number || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>Supplier:</strong> ${poData.supplier_name || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>PR Number:</strong> ${poData.purchase_request_number || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}</p>
          <p style="margin: 5px 0;"><strong>Total Items:</strong> ${items.length}</p>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #dee2e6; font-family: Arial, sans-serif; font-size: 14px;">
          <thead>
            <tr style="background-color: #0d6efd; color: #ffffff;">
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">No.</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #0d6efd; font-weight: bold;">Product</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">Qty</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #0d6efd; font-weight: bold;">Unit</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #0d6efd; font-weight: bold;">Total Cost</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
      <div style="margin-top: 24px;"></div>`;
  };

  const handleSend = async () => {
    const toList = toEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (!toList.length) {
      toast.error("Please enter at least one recipient email");
      return;
    }
    if (!subject.trim()) {
      toast.error("Please enter email subject");
      return;
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = body;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) {
      toast.error("Please enter email body");
      return;
    }

    try {
      const poDetailsTableHTML = generatePODetailsAndTableHTML();
      const fullEmailBody = poDetailsTableHTML + body;

      const result = await sendPurchaseOrderEmail({
        purchase_order_id: poId,
        to_emails: toList,
        cc_emails: ccEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        subject,
        body: fullEmailBody,
        attach_pdf: attachPdf,
        attachments,
      }).unwrap();
      toast.success(result?.message || "Email sent successfully");
      setAttachments([]);
      toggle();
    } catch (error: any) {
      const msg =
        error?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Failed to send email";
      toast.error(msg);
    }
  };

  const handleClose = () => {
    setToEmails("");
    setCcEmails("");
    setSubject("");
    setBody("");
    setAttachPdf(true);
    setAttachments([]);
    toggle();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="xl"
      style={{ maxWidth: "95vw", width: "1200px" }}
    >
      <ModalHeader toggle={handleClose} className="bg-light py-3">
        <div className="d-flex align-items-center">
          <i className="ri-mail-send-line text-primary me-2 fs-5"></i>
          <div>
            <h5 className="modal-title mb-0 fw-bold">Send Purchase Order Email</h5>
            <small className="text-muted">
              {poNumber} - Compose and send PO to supplier
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-0">
        {loadingPO ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading purchase order...</p>
          </div>
        ) : !poData ? (
          <Alert color="danger">Failed to load purchase order</Alert>
        ) : (
          <div className="p-4">
            {/* PO Details - Read-only */}
            <div className="mb-4">
              <h6 className="mb-3 fw-semibold">
                <i className="ri-file-list-3-line me-2"></i>
                Purchase Order Details
              </h6>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <small className="text-muted">PO Number</small>
                  <p className="mb-0 fw-medium">{poData.inventory_purchase_order_number}</p>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Supplier</small>
                  <p className="mb-0 fw-medium">{poData.supplier_name}</p>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">PR Number</small>
                  <p className="mb-0 fw-medium">{poData.purchase_request_number}</p>
                </div>
              </div>

              {/* Items Table - Read-only */}
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Unit</th>
                      <th className="text-end">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(poData.items || []).map((item: any, idx: number) => (
                      <tr key={item.id}>
                        <td className="text-center">{idx + 1}</td>
                        <td>{item.product_name || "N/A"}</td>
                        <td className="text-center">
                          {getSupplierEmailQty(item).toFixed(2)}
                        </td>
                        <td className="text-center">
                          {item.measurement_name || "-"}
                        </td>
                        <td className="text-end">
                          {(item.effective_total_item_cost ?? item.total_item_cost) != null
                            ? parseFloat(
                                String(item.effective_total_item_cost ?? item.total_item_cost)
                              ).toFixed(2)
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Email Form - Editable */}
            <hr />
            <h6 className="mb-3 fw-semibold">
              <i className="ri-mail-line me-2"></i>
              Email
            </h6>

            <FormGroup>
              <Label for="toEmails">To (Supplier Email)</Label>
              <Input
                id="toEmails"
                type="text"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="email@supplier.com"
              />
            </FormGroup>

            <FormGroup>
              <Label for="ccEmails">CC</Label>
              <Input
                id="ccEmails"
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="cc@example.com (comma-separated)"
              />
            </FormGroup>

            <FormGroup>
              <Label for="subject">Subject</Label>
              <Input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </FormGroup>

            <FormGroup>
              <Label>Body</Label>
              <div className="border rounded overflow-hidden">
                <CKEditor
                  editor={ClassicEditor as any}
                  data={body}
                  onChange={(_: any, editor: any) => setBody(editor.getData())}
                  config={{
                    toolbar: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "link",
                      "bulletedList",
                      "numberedList",
                      "|",
                      "blockQuote",
                      "insertTable",
                      "|",
                      "undo",
                      "redo",
                    ],
                  }}
                />
              </div>
            </FormGroup>

            <FormGroup check className="d-flex align-items-center gap-3">
              <div>
                <Input
                  type="checkbox"
                  id="attachPdf"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                />
                <Label for="attachPdf" check className="ms-1">
                  Attach PO PDF
                </Label>
              </div>
              {attachPdf && (
                <a
                  className="btn btn-sm btn-outline-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-order-pdf/${poNumber}${
                    poData?.po_status === "cancelled" || poData?.cancelled_at
                      ? "?cancellation=1"
                      : ""
                  }`}
                >
                  <i className="ri-download-2-line align-middle me-1"></i>
                  Download attached PDF
                </a>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label className="fw-semibold">
                Additional Attachments (optional)
              </Label>
              <Input
                type="file"
                multiple
                onChange={(e) =>
                  setAttachments(Array.from(e.target.files || []))
                }
              />
              {attachments.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small">
                  {attachments.map((f, idx) => (
                    <li
                      key={idx}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span className="text-truncate">{f.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0 ms-2"
                        onClick={() =>
                          setAttachments((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </FormGroup>
          </div>
        )}
      </ModalBody>

      {poData && (
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSend}
            disabled={isSending}
          >
            {isSending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill me-1"></i>
                Send Email
              </>
            )}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default POEmailModal;
